import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Grid,
  HStack,
  ListItem,
  Radio,
  Stack,
  Text,
  UnorderedList
} from '@chakra-ui/react'
import { CheckboxFormControl, RadioGroupFormControl } from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type {
  InsuranceParamsDto,
  InsuranceParamsLeasingDto,
  PolicyHolderDto,
} from 'BrokerTsLib/src/dtos'
import { Insurer, Selection } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useMemo } from 'react'

import { FormLabel } from '@/components/FormLabel'
import { InputGroup } from '@/components/InputGroup'
import { SectionHeading } from '@/components/Section/SectionHeading'
import { FormBody } from '@/containers/forms/FormBody'
import { PersonForm } from '@/containers/forms/PersonForm'
import { RadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { Apps } from '@/dtos'
import { useGetInsurerServiceTypes, useInsurers } from '@/hooks'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import { LabelHintIcon } from '@/icons'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'

export const InsureeForm = () => {
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const { currentPartner } = usePartner()

  const { isDisabled } = useInsuranceParams()

  const methods = useFormContext<
    InsuranceParamsDto | InsuranceParamsLeasingDto
  >()
  const {
    formState: { errors },
    register,
    setValue,
    watch,
    control
  } = methods

  const client = watch('client')
  const holderTypeValue = watch('policyHolder.holderSameAs')
  const ownerTypeValue = watch('policyHolder.ownerSameAs')
  const ownerType = watch('policyHolder.owner.type')

  const nameof = useNameof<InsuranceParamsDto>()
  const lnameof = useNameof<InsuranceParamsLeasingDto>()
  const phnameof = useNameof<PolicyHolderDto>(nameof((x) => x.policyHolder))

  const ownerOptions =
    useMemo(() => {
      const arr = [
        {
          label: 'Stejný s pojistníkem',
          value: Selection.PersonSameAsPolicyHolder,
          order: 1,
        },
        { label: 'Jiný', value: 0, order: 3 },
      ]

      if (holderTypeValue != Selection.PersonSameAsPolicyHolder)
        arr.push({
          label: 'Stejný s držitelem/provozovatelem',
          value: Selection.PersonSameAsKeeper,
          order: 2,
        })

      return arr.sort((x, y) => x.order - y.order)
    }, [holderTypeValue]) ?? []

  useEffect(() => {
    if (
      holderTypeValue == Selection.PersonSameAsPolicyHolder &&
      ownerTypeValue == Selection.PersonSameAsKeeper
    )
      setValue('policyHolder.ownerSameAs', Selection.PersonSameAsPolicyHolder)
  }, [holderTypeValue])

  const { data: allInsurers } = useInsurers(false, false)
  const insurers =
    useMemo(() => allInsurers?.data.map((x) => x.id), [allInsurers]) ?? []

  const { data: uniqaServiceTypes } = useGetInsurerServiceTypes(Insurer.Uniqa)
  const { data: csobServiceTypes } = useGetInsurerServiceTypes(Insurer.Csob)

  const hasVehicleMoreThanOneYear = watch(
    'policyHolder.hasVehicleMoreThanOneYear',
  )
  const propojistenost = watch('policyHolder.propojistenost')
  const phType = watch('policyHolder.person.type')

  const enableHasVehicleMoreThanOneYear =
    ownerTypeValue == Selection.PersonSameAsPolicyHolder ||
    holderTypeValue == Selection.PersonSameAsPolicyHolder
  useEffect(() => {
    if (hasVehicleMoreThanOneYear && !enableHasVehicleMoreThanOneYear)
      setValue('policyHolder.hasVehicleMoreThanOneYear', false)
  }, [enableHasVehicleMoreThanOneYear, hasVehicleMoreThanOneYear])

  const extraWizard = app == Apps.extra

  // nove umoznujeme pojistovat i jine pojistovny nez pouze subaru insurance
  const hidePartsForSubaruInsurance = false

  return (
    <>
      <FormBody>
        <PersonForm
          personPrefix={phnameof((x) => x.person)}
          disabled={isDisabled}
          showVatPayer={true}
        />

        {insurers.indexOf(Insurer.GeneraliCeskaPojistovna) >= 0 &&
          !isPublicPoleas &&
          isIn(phType, Selection.Company, Selection.SelfEmployed) && (
            <>
              <Stack direction="column">
                <FormLabel>
                  Splňuje pojistník alespoň 2 ze 3 níže uvedených podmínek?
                </FormLabel>

                <Box>
                  <UnorderedList spacing={1}>
                    <ListItem>
                      Je čistý obrat min. 13 600 000 EUR (cca 340 000 000 Kč)?
                    </ListItem>
                    <ListItem>
                      Je úhrn rozvahy min. 6 600 000 EUR (cca 165 000 000 Kč)?
                    </ListItem>
                    <ListItem>
                      Je průměrný roční stav zaměstnanců min. 250?
                    </ListItem>
                  </UnorderedList>
                </Box>

                <FormControl>
                  <RadioGroup
                    name="policyHolder.bigRiskCompany"
                    isDisabled={isDisabled}
                    onChangeValueTransform={(val) => val == '1'}
                    valueTransform={(val) =>
                      val == true ? '1' : val === false ? '0' : undefined
                    }
                    children={
                      <Stack direction="row" spacing={16}>
                        <Radio value={'1'}>Ano</Radio>
                        <Radio value={'0'}>Ne</Radio>
                      </Stack>
                    }
                  />
                </FormControl>
              </Stack>
            </>
          )}

        {!hidePartsForSubaruInsurance && !extraWizard && !isPublicPoleas && (
          <FormControl>
            <FormLabel>Zvýhodněná nabídka</FormLabel>

            <Stack spacing={4}>
              {insurers.indexOf(Insurer.Uniqa) >= 0 &&
                uniqaServiceTypes?.data &&
                uniqaServiceTypes.data.some((it) => it.name == 'UNIPOINT') && (
                  <CheckboxFormControl name='policyHolder.youngDriverComplicity' control={control} label='Spoluúčast mladého řidiče' isDisabled={isDisabled} />
                )}

              {insurers.indexOf(Insurer.Uniqa) >= 0 &&
                uniqaServiceTypes?.data &&
                uniqaServiceTypes.data.some((it) => it.name == 'WS') && (
                  <FormControl
                    isInvalid={!!errors.policyHolder?.hasVehicleMoreThanOneYear}
                  >
                    <Flex gap={2} alignItems="center">
                      <Checkbox
                        isChecked={hasVehicleMoreThanOneYear}
                        isDisabled={
                          isDisabled || !enableHasVehicleMoreThanOneYear
                        }
                        onChange={(e) =>
                          setValue(
                            'policyHolder.hasVehicleMoreThanOneYear',
                            e.target.checked,
                          )
                        }
                      >
                        Sleva za zkušenost UNIQA
                      </Checkbox>
                      <CustomTooltip
                        trigger="clickHover"
                        hasArrow
                        label={
                          <Stack>
                            <Text>
                              Pojišťovna <b>UNIQA</b> poskytuje slevu na
                              pojištění, pokud jsou splněny podmínky:
                            </Text>
                            <UnorderedList>
                              <ListItem>
                                Vozidlo nebylo za posledních 12 měsíců pojištěno
                                u UNIQA
                              </ListItem>
                              <ListItem>
                                Pojistník minimálně 2 roky neměl škodu(ověřuje
                                se automaticky)
                              </ListItem>
                              <ListItem>
                                Pojistník musí být shodný s provozovatelem nebo
                                vlastníkem
                              </ListItem>
                              <ListItem>
                                Vlastník / provozovatel vozidla je stejný
                                minimálně rok
                              </ListItem>
                              <ListItem>
                                Nutno doložit TP (Velký TP musí být doložen do
                                30 dnů)
                              </ListItem>
                            </UnorderedList>
                            <Text>
                              Poznámka:{' '}
                              <b>
                                tato sleva může být odebrána, pokud nejsou
                                splněny výše uvedené podmínky
                              </b>
                            </Text>
                          </Stack>
                        }
                        placement="top"
                      >
                        <Box sx={{ lineHeight: 0 }}>
                          <LabelHintIcon width={4} height={4} />
                        </Box>
                      </CustomTooltip>
                    </Flex>

                    {errors.policyHolder?.hasVehicleMoreThanOneYear && (
                      <FormErrorMessage>
                        {errors.policyHolder.hasVehicleMoreThanOneYear.message}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                )}

              {insurers.indexOf(Insurer.Csob) >= 0 && (
                <CheckboxFormControl name='policyHolder.csobBaScoring' control={control} label='ČSOB BA skóring' isDisabled={isDisabled} />
              )}

              {(insurers.findIndex(
                (x) => x == Insurer.Slavia || x == Insurer.Axa,
              ) >= 0 ||
                (insurers.findIndex((x) => x == Insurer.Csob) >= 0 &&
                  csobServiceTypes?.data &&
                  csobServiceTypes.data.some((it) => it.name == 'WS'))) && (
                  <CheckboxFormControl name='policyHolder.propojistenost' control={control} label='Sleva za propojištěnost' isDisabled={isDisabled} />
                )}

              {propojistenost && (
                <>
                  <Grid
                    templateColumns={{ xl: '1fr 1fr 1fr', lg: '1fr 1fr' }}
                    gap={16}
                  >
                    {insurers.findIndex((x) => x == Insurer.Csob) >= 0 &&
                      csobServiceTypes?.data &&
                      csobServiceTypes.data.some((it) => it.name == 'WS') && (
                        <FormControl>
                          <FormLabel>
                            Pojistná smlouva u ČSOB Pojišťovny
                          </FormLabel>
                          <InputGroup
                            {...register('policyHolder.propojistenostCSOB')}
                            isDisabled={isDisabled}
                            sx={{ width: '100%' }}
                          />
                        </FormControl>
                      )}

                    {insurers.findIndex((x) => x == Insurer.Axa) >= 0 && (
                      <FormControl>
                        <FormLabel>
                          Pojistná smlouva u Uniqa Pojišťovny (AXA)
                        </FormLabel>
                        <InputGroup
                          {...register('policyHolder.propojistenostAxa')}
                          isDisabled={isDisabled}
                          sx={{ width: '100%' }}
                        />
                      </FormControl>
                    )}

                    {insurers.findIndex((x) => x == Insurer.Slavia) >= 0 && (
                      <FormControl>
                        <FormLabel>
                          Pojistná smlouva u Slavia Pojišťovny
                        </FormLabel>
                        <InputGroup
                          {...register('policyHolder.propojistenostSlavia')}
                          isDisabled={isDisabled}
                          sx={{ width: '100%' }}
                        />
                      </FormControl>
                    )}
                  </Grid>
                </>
              )}
            </Stack>
          </FormControl>
        )}

        <Grid sx={{ w: '100%', gap: 8 }}>
          {!isPublicPoleas && <SectionHeading>Držitel/vlastník</SectionHeading>}

          {/* holder */}
          <FormControl>
            <FormLabel>Držitel/provozovatel vozidla</FormLabel>

            <FormHelperText>
              Pokud je pojistník odlišný od držitele, může znamenat přirážku na pojistném
              (Allianz, ČPP, Kooperativa, Uniqa)
            </FormHelperText>

            <RadioGroup
              {...register('policyHolder.holderSameAs')}
              ref={null}
              onChange={(e) =>
                setValue('policyHolder.holderSameAs', !e ? null : parseInt(e))
              }
              value={holderTypeValue?.toString() ?? ''}
              isDisabled={isDisabled}
            >
              <Radio value={Selection.PersonSameAsPolicyHolder.toString()}>
                Stejný s pojistníkem
              </Radio>
              <Radio value="">Jiný</Radio>
            </RadioGroup>
          </FormControl>

          {!holderTypeValue && (
            <PersonForm
              personPrefix={phnameof((x) => x.holder)}
              disabled={isDisabled}
            />
          )}

          {/* owner */}
          <FormControl>
            <FormLabel>Vlastník vozidla</FormLabel>

            <FormHelperText>
              Pokud je pojistník odlišný od vlastníka vozidla, může to znamenat přirážku
              na pojistném (Allianz, ČPP, Kooperativa, Uniqa)
            </FormHelperText>

            <RadioGroupFormControl
              name='policyHolder.ownerSameAs'
              valueAsNumber
              zeroAsNull
              isDisabled={isDisabled}
            >
              <HStack gap={16}>
                {ownerOptions.length > 0 &&
                  ownerOptions.map((item) => (
                    <Radio key={item.value} value={item.value?.toString()}>
                      {item.label}
                    </Radio>
                  ))}
              </HStack>
            </RadioGroupFormControl>
          </FormControl>

          {!ownerTypeValue && (
            <>
              <PersonForm
                personPrefix={phnameof((x) => x.owner)}
                disabled={isDisabled}
                showVatPayer={true}
              />

              {isIn(ownerType, Selection.Company, Selection.SelfEmployed) &&
                !isPublicPoleas &&
                <CheckboxFormControl name='vehicle.vehicleLeasing' control={control}
                  label={
                    currentPartner?.partner === 'Jap'
                      ? 'Vozidlo na financování'
                      : 'Vozidlo na leasing'
                  }
                  isDisabled={isDisabled}
                />
              }
            </>
          )}

          {ownerTypeValue === Selection.PersonSameAsKeeper && (
            <CheckboxFormControl name='policyHolder.holder.isVatPayer' control={control} label='Plátce DPH' isDisabled={isDisabled} />
          )}

          {client && (
            <>
              <FormControl>
                <FormLabel>Klient</FormLabel>
              </FormControl>

              <PersonForm
                personPrefix={lnameof((x) => x.client)}
                disabled={isDisabled}
                showVatPayer={true}
              />
            </>
          )}
        </Grid>
      </FormBody>
    </>
  )
}
